import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetEnrollmentQuery } from "@/redux/service";
import BaseBanner from "@/components_v2/Banner/BaseBanner";
import { CERTIFICATION_LIFECYCLE_STATUSES } from "@/domains/certifications/constants";
import SwitchPathModal from "@/domains/certifications/components/SwitchPathModal";
import { buildModuleOverviews } from "@/domains/certifications/progress";
import { getEnrollmentCredential } from "@/domains/certifications/status";
import {
  formatCertificationDate,
  getContentKey,
  getUpcomingLiveSessions,
} from "@/domains/certifications/utils";
import OverviewHeader from "@/features/certifications/overview/components/OverviewHeader";
import PathProgressCard from "@/features/certifications/overview/components/PathProgressCard";
import WeekList from "@/features/certifications/overview/components/WeekList";
import AboutProgramme from "@/features/certifications/overview/components/AboutProgramme";
import LiveSessions from "@/features/certifications/overview/components/LiveSessions";
import { CertificationOverviewProps } from "@/features/certifications/overview/types";
import "@/domains/certifications/components/styles.css";
import { certificationRouteKey } from "@/domains/certifications/routes";

const CertificationOverview = ({
  certification,
  progress,
}: CertificationOverviewProps) => {
  const { t } = useTranslation();
  const [isSwitchOpen, setIsSwitchOpen] = useState(false);
  const enrollment = useGetEnrollmentQuery(
    certification.enrollment?.id ?? skipToken,
  );

  const contentKey = getContentKey(certification.type);
  const weeks = useMemo(
    () => buildModuleOverviews(certification, progress, progress.selectedTrack),
    [certification, progress],
  );
  const currentTrack = certification.tracks.find(
    (track) => track.id === progress.selectedTrack,
  );
  const opensAt =
    certification.status === CERTIFICATION_LIFECYCLE_STATUSES.UPCOMING
      ? certification.startsAt
      : null;
  const otherTrack =
    certification.tracks.find((track) => track.id !== progress.selectedTrack) ??
    null;

  return (
    <div className="flex flex-col gap-700">
      <OverviewHeader certification={certification} contentKey={contentKey} />

      {currentTrack && (
        <PathProgressCard
          track={currentTrack}
          weeks={weeks}
          contentKey={contentKey}
          programmeTitle={certification.title}
          credential={
            enrollment.data ? getEnrollmentCredential(enrollment.data) : null
          }
          onSwitchPath={
            otherTrack && !certification.enrollment?.isLocked
              ? () => setIsSwitchOpen(true)
              : undefined
          }
        />
      )}

      <section className="flex flex-col gap-400">
        <h2 className="certification-heading header-l-medium">
          {t(`${contentKey}.sessionsTitle`)}
        </h2>

        {opensAt && (
          <BaseBanner
            title={t("certification.overview.startsOn.title", {
              date: formatCertificationDate(opensAt),
            })}
            description={t("certification.overview.startsOn.description")}
          />
        )}

        <WeekList
          slug={certificationRouteKey(certification)}
          weeks={weeks}
          contentKey={contentKey}
          opensAt={opensAt}
        />
      </section>

      <LiveSessions sessions={getUpcomingLiveSessions(certification)} />

      <AboutProgramme
        slug={certificationRouteKey(certification)}
        contentKey={contentKey}
      />

      <SwitchPathModal
        open={isSwitchOpen}
        certification={certification}
        targetTrack={otherTrack}
        onClose={() => setIsSwitchOpen(false)}
        onSwitched={() => setIsSwitchOpen(false)}
      />
    </div>
  );
};

export default CertificationOverview;
